import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Icon } from '@column/column-ui-kit';
import { Button, DatePicker, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { RenderFields, RestrictedBanner } from '~/components';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useNavigate } from '~/lib/navigation';
import { ReportingRepository } from '~/repositories';
import { NotificationPropsType, useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { getDateFormat } from '~/util';

// Mirrors the API: reports start no earlier than 2022-01-01 and cover at most 93 days.
const EARLIEST_REPORT_DATE = new Date(2022, 0, 1);
const MAX_RANGE_DAYS = 93;

const REPORT_TYPES = [
  { label: 'Bank account summary', value: 'bank_account_summary' },
  { label: 'Bank account transaction', value: 'bank_account_transaction' },
];

const DATE_RANGE_PRESETS = [
  { label: 'Today', value: 'today' },
  { label: 'Last 3 Days', value: 'last3Days' },
  { label: 'Last Week', value: 'lastWeek' },
  { label: 'Last Month', value: 'lastMonth' },
  { label: 'Custom', value: 'custom' },
] as const;

type DateRangePreset = (typeof DATE_RANGE_PRESETS)[number]['value'];

interface ScheduleReportForm {
  type: string;
  dateRangePreset: DateRangePreset;
  fromDate: Date | undefined;
  toDate: Date | undefined;
}

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const getPresetDates = (preset: Exclude<DateRangePreset, 'custom'>) => {
  const today = startOfDay(new Date());
  const from = new Date(today);

  if (preset === 'last3Days') {
    from.setDate(from.getDate() - 3);
  } else if (preset === 'lastWeek') {
    from.setDate(from.getDate() - 7);
  } else if (preset === 'lastMonth') {
    from.setMonth(from.getMonth() - 1);
  }

  return { fromDate: from, toDate: today };
};

const validateDateRange = (fromDate: Date | undefined, toDate: Date | undefined) => {
  if (!fromDate || !toDate) {
    return true;
  }
  if (fromDate > toDate) {
    return 'Start Date must be on or before End Date';
  }
  const lastAllowed = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate() + MAX_RANGE_DAYS - 1);
  return toDate <= lastAllowed || `Date range cannot exceed ${MAX_RANGE_DAYS} days`;
};

export const PageReportingSchedule: React.FC = () => {
  const currentPermission = useSessionStore((s) => s.currentPermission);
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { handleSubmitErrors } = useFormErrorHandler();
  const navigate = useNavigate();
  const canWrite = currentPermission?.reporting === 'write';
  const today = startOfDay(new Date());

  const formMethods = useForm<ScheduleReportForm>({
    defaultValues: {
      type: '',
      dateRangePreset: 'today',
      fromDate: today,
      toDate: today,
    },
  });

  const {
    handleSubmit,
    setValue,
    formState: { isSubmitted },
  } = formMethods;

  const scheduleReport = useMutation({
    mutationFn: (data: ScheduleReportForm) =>
      ReportingRepository.schedule({
        type: data.type,
        fromDate: data.fromDate ? getDateFormat(data.fromDate) : undefined,
        toDate: data.toDate ? getDateFormat(data.toDate) : undefined,
      }),
    onSuccess: () => {
      addSuccessNotification({ content: 'Report scheduled' });
      navigate(ROUTE.REPORTING);
    },
    onError: (error: Error & { code?: string }) => {
      const notification: NotificationPropsType = { content: error.message };

      // The API returns this when the platform has no reporting time zone set.
      if (error.code === 'feature_not_enabled') {
        notification.timeout = 8000;
        notification.actionButton = {
          label: 'Platform Settings',
          onClick: () => navigate(ROUTE.PLATFORM_SETTINGS),
        };
      }

      addDangerNotification(notification);
    },
  });

  const handlePresetChange = (preset: DateRangePreset) => {
    setValue('dateRangePreset', preset);

    if (preset !== 'custom') {
      const { fromDate, toDate } = getPresetDates(preset);
      // After a submit attempt, re-check so errors from the previous range clear.
      setValue('fromDate', fromDate, { shouldValidate: isSubmitted });
      setValue('toDate', toDate, { shouldValidate: isSubmitted });
    }
  };

  const handleSchedule = (data: ScheduleReportForm) => scheduleReport.mutate(data);

  const renderDatePicker = (value: unknown, onChange: (date: Date | undefined) => void, hasError: boolean) => (
    <DatePicker
      value={value instanceof Date ? value : undefined}
      onValueChange={(date) => {
        onChange(date);
        setValue('dateRangePreset', 'custom');
      }}
      disabledDays={[{ before: EARLIEST_REPORT_DATE }, { after: today }]}
      aria-invalid={hasError}
    />
  );

  const cancelButton = (
    <Button onClick={() => navigate(ROUTE.REPORTING)} variant="secondary" size="sm" type="button">
      Cancel
    </Button>
  );
  const scheduleButton = (
    <Button
      onClick={handleSubmit(handleSchedule, handleSubmitErrors)}
      disabled={!canWrite || scheduleReport.isPending}
      size="sm"
      type="button"
    >
      <Icon.Plus />
      {scheduleReport.isPending ? 'Scheduling…' : 'Schedule Report'}
    </Button>
  );

  return (
    <>
      <PageActions>
        {cancelButton}
        {scheduleButton}
      </PageActions>

      {!canWrite && (
        <div className="px-(--page-margin) pt-6">
          <RestrictedBanner />
        </div>
      )}

      <p className="m-0 max-w-[64ch] px-(--page-margin) pt-6 text-sm text-muted-foreground">
        Create a report for a date range or single day. Reports that include the current business day will be completed
        at the end of the day.
      </p>

      <FormProvider {...formMethods}>
        <form autoComplete="off" onSubmit={handleSubmit(handleSchedule, handleSubmitErrors)}>
          {RenderFields<ScheduleReportForm>({
            sections: [
              {
                fields: [
                  {
                    id: 'type',
                    label: 'Type',
                    rules: { required: 'Select a report Type' },
                    children: ({ value, onChange, onBlur }, { error }) => (
                      <Select
                        value={String(value ?? '')}
                        onValueChange={onChange}
                        onOpenChange={(open) => !open && onBlur()}
                      >
                        <SelectTrigger size="lg" className="w-full" aria-invalid={!!error}>
                          <SelectValue placeholder="Select a report type" />
                        </SelectTrigger>
                        <SelectContent>
                          {REPORT_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value} data-cy={`dropdown-option-${type.value}`}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ),
                  },
                  {
                    id: 'dateRangePreset',
                    label: 'Date Range Preset',
                    children: ({ value }) => (
                      <Select
                        value={String(value)}
                        onValueChange={(preset) => handlePresetChange(preset as DateRangePreset)}
                      >
                        <SelectTrigger size="lg" className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DATE_RANGE_PRESETS.map((preset) => (
                            <SelectItem
                              key={preset.value}
                              value={preset.value}
                              data-cy={`dropdown-option-${preset.value}`}
                            >
                              {preset.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ),
                  },
                  {
                    id: 'fromDate',
                    label: 'Start Date',
                    rules: { required: 'Select a Start Date', deps: ['toDate'] },
                    children: ({ value, onChange }, { error }) => renderDatePicker(value, onChange, !!error),
                  },
                  {
                    id: 'toDate',
                    label: 'End Date',
                    rules: {
                      required: 'Select an End Date',
                      validate: (value, form) => validateDateRange(form.fromDate, value as Date | undefined),
                    },
                    children: ({ value, onChange }, { error }) => renderDatePicker(value, onChange, !!error),
                  },
                ],
              },
            ],
          })}
        </form>
      </FormProvider>

      <div className="flex items-start justify-between px-(--page-margin) py-6">
        {cancelButton}
        {scheduleButton}
      </div>
    </>
  );
};
